import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { Input } from "@/components/ui/input";
import { AlertTriangle, BedDouble, CheckCircle2, Users2, ArrowRight, CalendarCheck } from "lucide-react";

// Utility: is this reservation flagged as missing info?
function isMissing(r) {
  return r.status === "eksik_bilgi";
}
function totalHeads(r) { return (r.adults || 0) + (r.children || 0) + (r.staff || 0); }
function totalRooms(r) { const x = r.rooms || {}; return (x.r1||0)+(x.r2||0)+(x.r3||0)+(x.r4||0)+(x.r5||0); }

const STATUS_TINT = {
  taslak: "bg-slate-100 text-slate-700",
  eksik_bilgi: "bg-orange-100 text-orange-800 ring-1 ring-orange-300",
  incelemede: "bg-amber-100 text-amber-800",
  onaylandi: "bg-emerald-100 text-emerald-800",
  iptal: "bg-rose-100 text-rose-700",
};
const STATUS_LABEL = {
  taslak: "Taslak",
  eksik_bilgi: "Eksik Bilgi",
  incelemede: "İncelemede",
  onaylandi: "Onaylı",
  iptal: "İptal",
};

export default function AcenteTodayCheckins() {
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const load = () => {
    panelApi
      .get("/reservations")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => rows
      .filter((r) => r.checkin === date && r.status !== "iptal")
      .sort((a, b) => (a.group_name || "").localeCompare(b.group_name || "")),
    [rows, date]
  );

  const missingList = useMemo(
    () => rows
      .filter((r) => isMissing(r))
      .sort((a, b) => (a.checkin || "").localeCompare(b.checkin || "")),
    [rows]
  );

  const totals = filtered.reduce(
    (a, r) => ({
      groups: a.groups + 1,
      heads: a.heads + totalHeads(r),
      rooms: a.rooms + totalRooms(r),
    }),
    { groups: 0, heads: 0, rooms: 0 }
  );

  return (
    <div className="space-y-6" data-testid="acente-today-checkins">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Bugün Girişler</div>
          <h1 className="font-serif text-3xl text-brand-navy">Giriş Yapan Gruplar</h1>
          <p className="text-sm text-slate-600 mt-1">Seçtiğiniz tarihe otele giriş yapan tüm gruplar tek listede — sağdan grup detayına gidin.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest text-slate-500">Tarih</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto" data-testid="today-date-picker" />
        </div>
      </header>

      {/* Totals */}
      <div className="grid gap-4 sm:grid-cols-3">
        <SummaryCard icon={CalendarCheck} label="Grup Sayısı" value={totals.groups} tone="from-emerald-400 to-emerald-600" />
        <SummaryCard icon={Users2} label="Toplam Kişi" value={totals.heads} tone="from-brand-orange to-brand-orange-dark" />
        <SummaryCard icon={BedDouble} label="Toplam Oda" value={totals.rooms} tone="from-brand-navy to-brand-navy" />
      </div>

      {/* Today's arrivals table */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-emerald-600" />
          <h2 className="font-serif text-lg text-brand-navy">{formatTr(date)} tarihinde giriş yapanlar</h2>
        </div>
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Bu tarihte giriş yapan grup yok.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-canvas-stone text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left py-2.5 px-4">Grup</th>
                <th className="text-left py-2.5 px-4">Rez.No</th>
                <th className="text-left py-2.5 px-4">Gece</th>
                <th className="text-left py-2.5 px-4">Yet/Çoc/Gör</th>
                <th className="text-left py-2.5 px-4">Oda Dağılımı</th>
                <th className="text-left py-2.5 px-4">Durum</th>
                <th className="text-right py-2.5 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const rm = r.rooms || {};
                return (
                  <tr
                    key={r.id}
                    className={`border-t border-slate-100 ${isMissing(r) ? "bg-orange-50" : "hover:bg-brand-orange/5"}`}
                    data-testid={`today-row-${r.id}`}
                  >
                    <td className="py-2.5 px-4 font-medium text-brand-navy">{r.group_name}</td>
                    <td className="py-2.5 px-4 font-mono text-xs">{r.reservation_number}</td>
                    <td className="py-2.5 px-4 text-xs">{r.nights} gece</td>
                    <td className="py-2.5 px-4 text-xs font-mono">{r.adults}/{r.children}/{r.staff}</td>
                    <td className="py-2.5 px-4 text-xs font-mono">1K:{rm.r1||0} · 2K:{rm.r2||0} · 3K:{rm.r3||0} · 4K:{rm.r4||0} · 5K:{rm.r5||0}</td>
                    <td className="py-2.5 px-4">
                      <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 inline-flex items-center gap-1 ${STATUS_TINT[r.status]}`}>
                        {isMissing(r) && <AlertTriangle size={10} />}
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <Link to={`/acente/rezervasyon/${r.id}`} className="text-brand-orange hover:text-brand-orange-dark inline-flex items-center gap-1 text-xs">
                        Aç <ArrowRight size={12} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Missing info section (visible to ops+admin) */}
      {missingList.length > 0 && (
        <div className="bg-orange-50 rounded-2xl border border-orange-200 overflow-hidden" data-testid="missing-info-section">
          <div className="p-4 border-b border-orange-200 flex items-center gap-2">
            <AlertTriangle size={16} className="text-orange-600" />
            <h2 className="font-serif text-lg text-orange-900">Eksik Bilgi Rezervasyonları ({missingList.length})</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-orange-100 text-xs uppercase text-orange-800">
              <tr>
                <th className="text-left py-2.5 px-4">Giriş</th>
                <th className="text-left py-2.5 px-4">Grup</th>
                <th className="text-left py-2.5 px-4">Acente</th>
                <th className="text-right py-2.5 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {missingList.map((r) => (
                <tr key={r.id} className="border-t border-orange-100" data-testid={`missing-row-${r.id}`}>
                  <td className="py-2.5 px-4 font-mono text-xs">{formatTr(r.checkin)}</td>
                  <td className="py-2.5 px-4">{r.group_name} <span className="text-xs text-slate-500 font-mono">({r.reservation_number})</span></td>
                  <td className="py-2.5 px-4 text-xs text-slate-600">{r.agency_id?.slice(0, 8)}…</td>
                  <td className="py-2.5 px-4 text-right">
                    <Link to={`/acente/rezervasyon/${r.id}/duzenle`} className="text-orange-700 hover:text-orange-800 inline-flex items-center gap-1 text-xs">
                      Tamamla <ArrowRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, tone }) {
  return (
    <div className={`rounded-2xl p-5 text-white bg-gradient-to-br ${tone} shadow-md`}>
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-xl bg-white/25 flex items-center justify-center"><Icon size={18} /></div>
        <div className="text-[10px] uppercase tracking-widest text-white/85">{label}</div>
      </div>
      <div className="font-serif text-4xl mt-4">{value}</div>
    </div>
  );
}
